import { InjectionToken } from '@angular/core';
import { PERM } from './app-permissions';
import { MODULE_SETTING } from './module-settings';

/** Aligné sur `AuthService.hasPermission` pour le filtrage du menu. */
export type PermissionMode = 'all' | 'any';

/**
 * Définition partagée : menu latéral (`AppMenu`) et matrice droits par écran (`roles-create`).
 * Les chaînes doivent correspondre exactement aux `name` renvoyés par l’API Spatie.
 */
export interface AppMenuLeafDef {
  label: string;
  icon: string;
  /** Ordre d’affichage (plus petit = plus haut). Si absent, tri après ceux avec ordre. */
  order?: number;
  routerLink?: string;
  target?: '_blank';
  permission?: string | string[];
  permissionMode?: PermissionMode;
  requireSetting?: string;
  /** Droits « actions » (création, modification, gestion…) pour l’éditeur de rôles */
  actionPermissions?: readonly string[];
  /** Entrée sans droit à cocher (ex. tableau de bord ouvert à tous) */
  roleEditorSkip?: boolean;
  /** Réservé aux super-admins uniquement (pas de permission Spatie, rôle seul) */
  superAdminOnly?: boolean;
  /** Visible uniquement si l'utilisateur possède au moins un des rôles listés */
  roles?: string | string[];
  /** Code du module MONICASE (TRP) derrière l'entrée : masquée si MONICASE masque ce module. */
  module?: string;
}

export interface AppMenuSectionDef {
  /** Libellé groupe dans la sidebar (peut être vide) */
  label: string;
  icon: string;
  /** Ordre d’affichage (plus petit = plus haut). Si absent, tri après ceux avec ordre. */
  order?: number;
  requireSetting?: string;
  /** Titre dans l’accordéon « droits par menu » (si vide, dérivé côté UI) */
  accordionLabel?: string;
  items: readonly AppMenuLeafDef[];
}

export const APP_MENU_SECTIONS: readonly AppMenuSectionDef[] = [
  {
    label: '',
    icon: 'text-xl fa-light fa-gauge',
    accordionLabel: 'Accueil & vente',
    items: [
      {
        label: 'Tableau de bord',
        icon: 'text-xl fa-light fa-gauge',
        routerLink: '/moni/dashboard',
        roleEditorSkip: true,
      },
      {
        label: 'Caisse (POS)',
        icon: 'text-xl fa-light fa-cash-register',
        routerLink: '/pos',
        target: '_blank',
        permission: [PERM.createOrders, PERM.viewOrders],
        permissionMode: 'any',
        requireSetting: MODULE_SETTING.polisi,
        module: 'polisi',
      },
    ],
  },
  {
    label: 'Contacts',
    icon: 'text-xl fa-light fa-box',
    items: [
      {
        label: 'Particuliers',
        icon: 'text-xl fa-light fa-users',
        routerLink: '/moni/clients',
        permission: PERM.viewClients,
        actionPermissions: [PERM.createClients, PERM.updateClients, PERM.deleteClients],
      },
      {
        label: 'Entreprises',
        icon: 'text-xl fa-light fa-building',
        routerLink: '/moni/companies',
        permission: PERM.viewCompanies,
        actionPermissions: [PERM.createCompanies, PERM.updateCompanies, PERM.deleteCompanies],
      },
    ],
  },
  {
    label: 'Achats',
    icon: 'text-xl fa-light fa-truck',
    items: [
      {
        label: 'Fournisseurs',
        icon: 'text-xl fa-light fa-truck',
        routerLink: '/moni/fournisseurs',
        permission: PERM.viewSuppliers,
        actionPermissions: [PERM.createSuppliers, PERM.updateSuppliers, PERM.deleteSuppliers],
      },
    ],
  },
  {
    label: 'Catalogue',
    icon: 'text-xl fa-light fa-box',
    items: [
      {
        label: 'Produits',
        icon: 'text-xl fa-light fa-box',
        routerLink: '/moni/produits',
        permission: PERM.viewProducts,
        actionPermissions: [PERM.createProducts, PERM.updateProducts, PERM.deleteProducts],
      },
      {
        label: 'Catégories',
        icon: 'text-xl fa-light fa-tags',
        routerLink: '/moni/categories',
        permission: PERM.viewCategories,
        actionPermissions: [PERM.createCategories, PERM.updateCategories, PERM.deleteCategories],
      },
      {
        label: 'Code Coupons',
        icon: 'text-xl fa-light fa-badge-dollar',
        routerLink: '/moni/reductions',
        permission: PERM.viewReductions,
        actionPermissions: [PERM.createReductions, PERM.updateReductions, PERM.deleteReductions],
      },
    ],
  },
  {
    label: 'Ventes',
    icon: 'text-xl fa-light fa-shopping-cart',
    items: [
      {
        label: 'Devis',
        icon: 'text-xl fa-light fa-file-edit',
        routerLink: '/moni/devis',
        permission: PERM.viewQuotes,
        actionPermissions: [PERM.createQuotes, PERM.updateQuotes, PERM.deleteQuotes],
      },
      {
        label: 'Commandes',
        icon: 'text-xl fa-light fa-shopping-cart',
        routerLink: '/moni/ventes',
        permission: PERM.viewOrders,
        actionPermissions: [PERM.createOrders, PERM.updateOrders, PERM.deleteOrders],
      },
      {
        label: 'Factures',
        icon: 'text-xl fa-light fa-file',
        routerLink: '/moni/factures',
        permission: PERM.viewInvoices,
        actionPermissions: [PERM.createInvoices, PERM.updateInvoices, PERM.deleteInvoices],
      },
      {
        label: 'Factures recurrentes',
        icon: 'text-xl fa-light fa-sync',
        routerLink: '/moni/factures-recurrentes',
        permission: PERM.viewInvoices,
        actionPermissions: [PERM.createInvoices, PERM.updateInvoices, PERM.deleteInvoices],
      },
      {
        label: 'Abonnements',
        icon: 'text-xl fa-light fa-rotate',
        routerLink: '/moni/abonnements',
        permission: PERM.viewSubscriptions,
        actionPermissions: [PERM.createSubscriptions, PERM.updateSubscriptions, PERM.deleteSubscriptions],
      },
    ],
  },
  // « Comptabilité » est désormais une application dédiée (LOGISTIKI) avec sa propre
  // interface et sa propre sidebar — accessible via l'app-launcher (route /logistiki).
  {
    label: 'Paramètres',
    icon: 'text-xl fa-light fa-cog',
    items: [
      {
        label: 'Taxes / TVA',
        icon: 'text-xl fa-light fa-badge-percent',
        routerLink: '/moni/taxes',
        permission: PERM.viewTaxes,
        actionPermissions: [PERM.createTaxes, PERM.updateTaxes, PERM.deleteTaxes],
      },
      {
        label: 'Devises',
        icon: 'text-xl fa-light fa-coins',
        routerLink: '/moni/devises',
        permission: PERM.viewSettings,
        actionPermissions: [PERM.updateSettings],
      },
      {
        label: 'Attributs personnalisés',
        icon: 'text-xl fa-light fa-tags',
        routerLink: '/moni/attributs-personnalises',
        permission: PERM.viewSettings,
        actionPermissions: [PERM.updateSettings],
      },
      {
        label: 'Langues',
        icon: 'text-xl fa-light fa-language',
        routerLink: '/moni/languages',
        permission: PERM.viewSettings,
        actionPermissions: [PERM.updateSettings],
      },
      {
        label: 'Types de livraison',
        icon: 'text-xl fa-light fa-truck',
        routerLink: '/moni/livraisons',
        permission: PERM.viewOptions,
        roleEditorSkip: true,
      },
      {
        label: 'Moyens de paiement',
        icon: 'text-xl fa-light fa-credit-card',
        routerLink: '/moni/moyens-de-paiement',
        permission: PERM.viewPaymentMethods,
        actionPermissions: [PERM.createPaymentMethods, PERM.updatePaymentMethods, PERM.deletePaymentMethods],
      },
      {
        label: 'Médiathèque',
        icon: 'text-xl fa-light fa-images',
        routerLink: '/moni/media-library',
        superAdminOnly: true,
      },
    ],
  },
  {
    label: 'Emails',
    icon: 'text-xl fa-light fa-envelope',
    items: [
      {
        label: 'Test d\'envoi',
        icon: 'text-xl fa-light fa-send',
        routerLink: '/moni/email-test',
        superAdminOnly: true,
      },
      {
        label: 'Modèles de contenu',
        icon: 'text-xl fa-light fa-file-edit',
        routerLink: '/moni/email-contents',
        superAdminOnly: true,
      },
      {
        label: 'Template visuel',
        icon: 'text-xl fa-light fa-palette',
        routerLink: '/moni/email-template',
        superAdminOnly: true,
      },
      {
        label: 'Quota Email',
        icon: 'text-xl fa-light fa-chart-pie',
        routerLink: '/moni/brevo',
        roleEditorSkip: true,
      },
    ],
  },
  {
    label: 'Administration',
    icon: 'text-xl fa-light fa-shield',
    items: [
      {
        label: 'Utilisateurs',
        icon: 'text-xl fa-light fa-user-edit',
        routerLink: '/moni/utilisateurs',
        permission: PERM.viewUsers,
        actionPermissions: [PERM.createUsers, PERM.updateUsers, PERM.assignRoles, PERM.assignPermissions],
      },
      {
        // Réservée au super-admin (BREAD) et à l'Administrateur (BRE) : la règle
        // tient au rôle, aucune permission ne la délègue — d'où `roles` plutôt
        // que `permission`, et `roleEditorSkip` pour ne pas la proposer dans
        // l'éditeur de rôles (les permissions `* shops` y sont déjà exclues,
        // cf. ROLES_EDITOR_EXCLUDED_PERMS).
        label: 'Boutiques',
        icon: 'text-xl fa-light fa-store',
        routerLink: '/moni/boutiques',
        roles: ['super-admin', 'Administrateur'],
        roleEditorSkip: true,
      },
      {
        label: 'Rôles & permissions',
        icon: 'text-xl fa-light fa-lock',
        routerLink: '/moni/roles',
        permission: [PERM.assignRoles, PERM.assignPermissions],
        permissionMode: 'any',
        roleEditorSkip: true,
      },
      {
        label: 'Mon abonnement',
        icon: 'text-xl fa-light fa-credit-card',
        routerLink: '/moni/mon-abonnement',
        roles: ['super-admin', 'Administrateur'],
        roleEditorSkip: true,
      },
      {
        label: 'Paramètres généraux',
        icon: 'text-xl fa-light fa-gear',
        routerLink: '/moni/settings',
        roles: ['super-admin', 'Administrateur'],
        permission: PERM.viewSettings,
        actionPermissions: [PERM.createSettings, PERM.updateSettings, PERM.deleteSettings],
      },
      {
        label: 'Groupes de paramètres',
        icon: 'text-xl fa-light fa-gear',
        routerLink: '/moni/settings/groups',
        superAdminOnly: true,
      },
    ],
  },
  {
    label: 'Aide',
    icon: 'fa-light fa-question-circle',
    items: [
      {
        label: "Centre d'aide",
        icon: 'text-xl fa-light fa-question-circle',
        routerLink: '/aide',
        roleEditorSkip: true,
      },
    ],
  },
];

/**
 * Définition du menu injectable pour permettre une surcharge côté `custom`.
 * Par défaut, utilise `APP_MENU_SECTIONS`.
 */
export const APP_MENU_DEFINITION = new InjectionToken<readonly AppMenuSectionDef[]>('APP_MENU_DEFINITION', {
  providedIn: 'root',
  factory: () => APP_MENU_SECTIONS,
});

/**
 * Permissions internes exclues de l'éditeur de rôles (boutique, tarifs, options système…).
 * Elles restent fonctionnelles côté API mais n'ont pas vocation à être assignées manuellement.
 */
export const ROLES_EDITOR_EXCLUDED_PERMS = new Set<string>([
  'view shops', 'create shops', 'update shops', 'delete shops',
  'view product prices', 'manage product prices',
  'view options', 'create options', 'update options', 'delete options',
  'view medias', 'create medias', 'update medias', 'delete medias',
]);

/** Noms de permissions référencés par la structure menu (pour détecter les orphelins côté API). */
export function collectMenuPermissionNames(): Set<string> {
  const set = new Set<string>();
  for (const section of APP_MENU_SECTIONS) {
    for (const item of section.items) {
      const p = item.permission;
      if (p !== undefined) {
        if (Array.isArray(p)) p.forEach((x) => set.add(x));
        else set.add(p);
      }
      item.actionPermissions?.forEach((x) => set.add(x));
    }
  }
  return set;
}
